<script setup lang="ts">
/**
 * Główny komponent aplikacji.
 * Zawiera layout z NavBar, router-view i Footer.
 * Widok gry (PlayView) renderuje się bez layoutu.
 */
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { SESSION_EXPIRED_EVENT } from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import { useLobbyStore, type PresenceState } from '@/stores/lobby'
import NavBar from '@/components/layout/NavBar.vue'
import Footer from '@/components/layout/Footer.vue'
import NotificationToast from '@/components/common/NotificationToast.vue'
import UploadProgressStack from '@/components/common/UploadProgressStack.vue'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const lobby = useLobbyStore()

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  return Boolean(target.closest('input, textarea, [contenteditable="true"], [data-native-context]'))
}

function preventUnusedContextMenu(event: MouseEvent): void {
  if (!isEditableTarget(event.target)) event.preventDefault()
}

function preventUnusedNativeDrag(event: DragEvent): void {
  const target = event.target
  if (!(target instanceof Element)) {
    event.preventDefault()
    return
  }
  if (target.closest('[draggable="true"], input[type="file"], [data-native-drag]')) return
  event.preventDefault()
}

function handleSessionExpired(): void {
  const redirect = route.meta.requiresAuth ? route.fullPath : ''
  auth.invalidateSession()
  if (route.name !== 'login') {
    void router.replace({
      name: 'login',
      query: redirect ? { redirect, reason: 'session-expired' } : { reason: 'session-expired' },
    })
  }
}

function presenceForRoute(): PresenceState {
  if (route.name === 'lobby') return { activity: 'available', tableId: null }
  if (route.name === 'play') {
    const routeId = route.params.id
    return {
      activity: 'table',
      tableId: typeof routeId === 'string' ? routeId : null,
    }
  }
  if (route.path === '/armies' || route.path.startsWith('/armies/')) {
    return { activity: 'armies', tableId: null }
  }
  return { activity: 'unavailable', tableId: null }
}

const stopPresenceWatch = watch(
  () => [auth.isAuthenticated, route.name, route.params.id, route.path] as const,
  ([authenticated]) => {
    if (!authenticated) {
      lobby.stopHeartbeat()
      return
    }
    lobby.startHeartbeat(presenceForRoute())
  },
  { immediate: true },
)

onMounted(() => {
  window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired)
  document.addEventListener('contextmenu', preventUnusedContextMenu)
  document.addEventListener('dragstart', preventUnusedNativeDrag)
})
onBeforeUnmount(() => {
  stopPresenceWatch()
  lobby.stopHeartbeat()
  window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired)
  document.removeEventListener('contextmenu', preventUnusedContextMenu)
  document.removeEventListener('dragstart', preventUnusedNativeDrag)
})

/* Gra wyświetla się pełnoekranowo bez nawigacji */
const isPlayView = computed(() => route.name === 'play')
const hideLayoutOnAuth = computed(() => {
  const authViews = new Set([
    'login',
    'register',
    'password-reset',
    'password-reset-confirm',
    'forgot-username',
    'activate',
  ])
  return authViews.has(String(route.name ?? ''))
})
</script>

<template>
  <!-- SVG Filter Definition for smoother text-stroke rendering -->
  <svg class="svg-filter" aria-hidden="true" style="position: absolute; width: 0; height: 0; pointer-events: none;">
    <defs>
      <filter id="goo">
        <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" result="blur" />
        <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 19 -9" result="goo" />
        <feComposite in="SourceGraphic" in2="goo" operator="atop"/>
      </filter>
    </defs>
  </svg>

  <!-- Full-screen game view — no nav/footer -->
  <div v-if="isPlayView" class="h-screen w-screen overflow-hidden">
    <RouterView />
  </div>

  <!-- Standard layout -->
  <div v-else class="min-h-screen flex flex-col"
    :style="{
      backgroundImage: `url('/media/site/new_york.avif')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed'
    }">
    <NavBar v-if="!hideLayoutOnAuth" />
    <main
      :class="hideLayoutOnAuth
        ? 'flex-1 flex items-center justify-center px-4 py-6'
        : 'flex-1 container mx-auto px-4 py-6'"
    >
      <RouterView />
    </main>
    <Footer />
  </div>

  <!-- Global toast notifications -->
  <NotificationToast />
  <UploadProgressStack />
</template>
